export type CookieConsent = {
    analytics: boolean;
    personalization: boolean;
    marketing: boolean;
    consentId: string;
    policyVersion: string;
    updatedAtUtc: string;
};

export type CookieConsentSource = 'accept_all' | 'save_preferences' | 'reject_optional' | 'sync';

export type CookieConsentChangedDetail = {
    consent: CookieConsent;
    previous: CookieConsent | null;
    source: CookieConsentSource;
};

export const CONSENT_KEY = 'japonics_cookie_consent';
export const CULTURE_COOKIE_NAME = '.AspNetCore.Culture';
export const CONSENT_CHANGED_EVENT = 'japonics:consent-changed';
export const CONSENT_POLICY_VERSION = '2026-07-12';

const DEFAULT_CONSENT: CookieConsent = {
    analytics: false,
    personalization: false,
    marketing: false,
    consentId: '',
    policyVersion: CONSENT_POLICY_VERSION,
    updatedAtUtc: ''
};

function createConsentId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const values = new Uint32Array(4);
        crypto.getRandomValues(values);
        return Array.from(values, value => value.toString(16).padStart(8, '0')).join('-');
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function getCookie(name: string): string | null {
    const prefix = `${name}=`;
    const item = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(prefix));

    if (!item) return null;

    try {
        return decodeURIComponent(item.slice(prefix.length));
    } catch {
        return item.slice(prefix.length);
    }
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

export function deleteCookie(name: string): void {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
}

export function setCultureCookie(culture: string): void {
    if (!/^[a-z]{2}$/i.test(culture)) return;

    writeCookie(CULTURE_COOKIE_NAME, `c=${culture.toLowerCase()}|uic=${culture.toLowerCase()}`, 60 * 60 * 24 * 365);
}

function normalizeConsent(value: Partial<CookieConsent> | null, existing?: CookieConsent | null): CookieConsent | null {
    if (!value) return null;

    return {
        analytics: value.analytics === true,
        personalization: value.personalization === true,
        marketing: value.marketing === true,
        consentId: typeof value.consentId === 'string' && value.consentId.trim()
            ? value.consentId.trim()
            : existing?.consentId || createConsentId(),
        policyVersion: typeof value.policyVersion === 'string' && value.policyVersion.trim()
            ? value.policyVersion.trim()
            : CONSENT_POLICY_VERSION,
        updatedAtUtc: typeof value.updatedAtUtc === 'string' && value.updatedAtUtc.trim()
            ? value.updatedAtUtc.trim()
            : existing?.updatedAtUtc || new Date().toISOString()
    };
}

function parseConsent(raw: string | null): CookieConsent | null {
    if (!raw) return null;

    try {
        return normalizeConsent(JSON.parse(raw) as Partial<CookieConsent>);
    } catch {
        return null;
    }
}

function readLocalConsent(): string | null {
    try {
        return localStorage.getItem(CONSENT_KEY);
    } catch {
        return null;
    }
}

function writeLocalConsent(consent: CookieConsent): void {
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    } catch {
        // Persisting in the cookie is enough when localStorage is unavailable.
    }
}

export function readCookieConsent(): CookieConsent | null {
    const localConsent = parseConsent(readLocalConsent());
    if (localConsent) {
        writeCookie(CONSENT_KEY, JSON.stringify(localConsent), 60 * 60 * 24 * 365);
        return localConsent;
    }

    const cookieConsent = parseConsent(getCookie(CONSENT_KEY));
    if (cookieConsent) {
        writeLocalConsent(cookieConsent);
        return cookieConsent;
    }

    return null;
}

export function hasStoredCookieConsent(): boolean {
    return readCookieConsent() !== null;
}

export function hasAnalyticsConsent(): boolean {
    return readCookieConsent()?.analytics === true;
}

export function hasPersonalizationConsent(): boolean {
    return readCookieConsent()?.personalization === true;
}

export function setCookieConsent(consent: Partial<CookieConsent>, source: CookieConsentSource): CookieConsent {
    const previous = readCookieConsent();
    const normalized = normalizeConsent({
        ...DEFAULT_CONSENT,
        ...consent,
        consentId: previous?.consentId || consent.consentId,
        policyVersion: CONSENT_POLICY_VERSION,
        updatedAtUtc: new Date().toISOString()
    }, previous) ?? DEFAULT_CONSENT;

    writeLocalConsent(normalized);
    writeCookie(CONSENT_KEY, JSON.stringify(normalized), 60 * 60 * 24 * 365);

    if (!normalized.personalization) {
        deleteCookie(CULTURE_COOKIE_NAME);
    }

    if (!normalized.analytics) {
        deleteCookie('_ga');
        deleteCookie('_gid');
        deleteCookie('_gat');
        const measurementId = document.body.getAttribute('data-ga4-measurement-id') ?? '';
        if (measurementId.startsWith('G-')) {
            deleteCookie(`_ga_${measurementId.slice(2)}`);
        }
    }

    window.dispatchEvent(new CustomEvent<CookieConsentChangedDetail>(CONSENT_CHANGED_EVENT, {
        detail: {
            consent: normalized,
            previous,
            source
        }
    }));
    recordConsent(normalized, source);

    return normalized;
}

function recordConsent(consent: CookieConsent, source: CookieConsentSource): void {
    const payload = JSON.stringify({
        consentId: consent.consentId,
        policyVersion: consent.policyVersion,
        updatedAtUtc: consent.updatedAtUtc,
        source,
        consent: {
            analytics: consent.analytics,
            personalization: consent.personalization,
            marketing: consent.marketing
        }
    });

    try {
        void fetch('/api/consent', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: payload,
            credentials: 'same-origin',
            keepalive: true
        });
    } catch {
        // Consent persistence in the browser remains the source for runtime behavior.
    }
}
